import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import moment from "moment";
import React from "react";

import Banner from "@khanacademy/wonder-blocks-banner";
import {
    Breadcrumbs,
    BreadcrumbsItem,
} from "@khanacademy/wonder-blocks-breadcrumbs";
import {View} from "@khanacademy/wonder-blocks-core";
import {Checkbox} from "@khanacademy/wonder-blocks-form";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {HeadingLarge} from "@khanacademy/wonder-blocks-typography";
import {
    DateRangeDropdown,
    type DateRangeSelection,
    type PartialDateRangeSelection,
} from "@khan/component-date-range-dropdown";

interface ConversationViewHeadingProps {
    profilePath?: string;
    dateRangeSelection: PartialDateRangeSelection;
    setDateRangeSelection: (
        value: React.SetStateAction<DateRangeSelection>,
    ) => void;
    flaggedThreadsOnly: boolean;
    setFlaggedThreadsOnly: (value: React.SetStateAction<boolean>) => void;
}

// As designed
const DATE_FILTER_WIDTH = 288;
const FILTERS_MARGIN_TOP = 35;

/**
 * Displays the heading for the Khanmigo conversations view, including breadcrumbs,
 * title, information banner, and filtering options.
 *
 * @param profilePath - Optional path to the user's profile for the breadcrumb navigation
 * @param dateRangeSelection - Selected date range for filtering conversations
 * @param setDateRangeSelection - Function to update the date range selection
 * @param flaggedThreadsOnly - Boolean indicating whether to show only flagged threads
 * @param setFlaggedThreadsOnly - Function to update the flagged threads filter
 * @returns React component for the conversations view heading
 */
export function ConversationsViewHeading({
    profilePath,
    dateRangeSelection,
    setDateRangeSelection,
    flaggedThreadsOnly,
    setFlaggedThreadsOnly,
}: ConversationViewHeadingProps): React.ReactNode {
    return (
        <>
            {" "}
            {profilePath && (
                <Breadcrumbs>
                    <BreadcrumbsItem>
                        <Link href={profilePath}>{t`Profile`}</Link>
                    </BreadcrumbsItem>
                </Breadcrumbs>
            )}
            <HeadingLarge
                style={{
                    marginBlockStart: spacing.xxxSmall_4,
                    marginBlockEnd: spacing.small_12,
                }}
            >
                {t`Khanmigo Chat History`}
            </HeadingLarge>
            <Strut size={spacing.xSmall_8} />
            <Banner
                text={t`Conversations with Khanmigo are retained for up to one year. If flagged for review, they may be retained for up to two years.`}
                layout="full-width"
            />
            <View style={styles.filtersContainer}>
                <DateRangeDropdown
                    onChange={setDateRangeSelection}
                    dateRangeSelection={dateRangeSelection}
                    style={styles.dateRangeDropdown}
                    maxDate={moment()}
                />
                <Strut size={spacing.large_24} />
                <Checkbox
                    label={t`Only show flagged chats`}
                    checked={flaggedThreadsOnly}
                    onChange={setFlaggedThreadsOnly}
                />
            </View>
        </>
    );
}

const styles = StyleSheet.create({
    dateRangeDropdown: {
        width: DATE_FILTER_WIDTH,
    },
    filtersContainer: {
        flexDirection: "row",
        marginBlockStart: FILTERS_MARGIN_TOP,
        alignItems: "center",
    },
});
